import { useEffect, useMemo, useState } from 'react';
import SearchableDropdown from './SearchableDropdown';
import axiosInstance from '../axios/axiosInstance';
import apiEndpoints from '../axios/api';
import { selectedSocietyStorer } from '../utils/webStorer';

// Login-role picker for the Add / Edit Owner, Tenant and Family forms.
//
// Options come from GET /occupants/assignable-roles, which returns only the
// roles the signed-in user may give: every role for a Society Admin, member
// roles (Resident, committee, wing / floor representative) for anyone else who
// manages members. The API enforces the same rule on save. RoleSelect is still
// used for vendors; it reads the admin-only roles list.

// One request per page load is plenty - the list only changes when roles do.
let cache = { key: null, promise: null };

export const fetchAssignableRoles = (params = {}) => {
    const societyId = selectedSocietyStorer.getSocietyId();
    const key = `${societyId}|${selectedSocietyStorer.getRoleId?.() ?? ''}|${params.source_type || ''}|${params.source_row_id || ''}`;
    if (cache.key !== key || !cache.promise) {
        cache = {
            key,
            promise: axiosInstance
                .get(apiEndpoints.GET_ASSIGNABLE_MEMBER_ROLES_API, { params: { society_id: societyId, ...params } })
                .then(res => res?.data || { roles: [] })
                .catch(() => {
                    cache = { key: null, promise: null };
                    return { roles: [] };
                }),
        };
    }
    return cache.promise;
};

const MemberRoleSelect = ({ value, onChange, placeholder = 'Default: Resident', disabled = false, className = '' }) => {
    const [roles, setRoles] = useState([]);
    const [loading, setLoading] = useState(true);

    useEffect(() => {
        let cancelled = false;
        fetchAssignableRoles().then(data => {
            if (cancelled) return;
            setRoles(Array.isArray(data?.roles) ? data.roles : []);
            setLoading(false);
        });
        return () => { cancelled = true; };
    }, []);

    const options = useMemo(() => roles.map(r => ({
        role_id: r.role_id,
        label: r.is_system_role === 0 || r.society_id ? `${r.role_name} (custom)` : r.role_name,
    })), [roles]);

    return (
        <SearchableDropdown
            value={value || ''}
            onChange={onChange}
            options={options}
            valueKey="role_id"
            labelKey="label"
            placeholder={loading ? 'Loading…' : placeholder}
            loading={loading}
            disabled={disabled || loading}
            className={className}
        />
    );
};

export default MemberRoleSelect;
